import { useState, useMemo } from 'react';
import {
  ReportShapeItem,
  ReportType,
  ShapeType,
  FilterType,
} from '../../../reportsTypes';

/**
 * Derive and manage current shapeType / filterType state from reportType definition.
 */
interface ExternalState<T> {
  value?: T;
  onChange?: (next: T) => void;
}

interface UseShapeAndFilterTypesOptions {
  shapeType?: ExternalState<ShapeType>;
  filterType?: ExternalState<FilterType>;
}

export function useShapeAndFilterTypes(
  reportType: ReportType,
  options?: UseShapeAndFilterTypesOptions
) {
  const [internalFilterType, internalSetFilterType] = useState<FilterType>(
    () => {
      if (!reportType.filter) return 'normal';
      if ('ship' in reportType.filter) return 'ship';
      if ('shiplist' in reportType.filter) return 'shiplist';
      if ('combined' in reportType.filter) return 'combined';
      return 'normal';
    }
  );

  const [internalShapeType, internalSetShapeType] = useState<ShapeType>(() => {
    if (!reportType.shape) return 'shape';
    if ('multiShape' in reportType.shape) return 'multiShape';
    if ('ports' in reportType.shape) return 'ports';
    if ('worldShape' in reportType.shape) return 'worldShape';
    return 'shape';
  });

  const filterType = options?.filterType?.value ?? internalFilterType;
  const setFilterType = options?.filterType?.onChange ?? internalSetFilterType;

  const shapeType = options?.shapeType?.value ?? internalShapeType;
  const setShapeType = options?.shapeType?.onChange ?? internalSetShapeType;

  const currentShapeItems = useMemo(() => {
    if (!reportType.shape) return undefined;
    return reportType.shape[shapeType] as ReportShapeItem[] | undefined;
  }, [reportType.shape, shapeType]);

  return {
    filterType,
    setFilterType,
    shapeType,
    setShapeType,
    currentShapeItems,
  };
}
